import React, { useMemo } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// components
import AampeTooltip from '../../shared/AampeTooltip/AampeTooltip';

export interface FormulaChipItem {
    id: number | string;
    name: string;
}

interface FormulaChipProps {
    items?: FormulaChipItem[];
    showAll?: boolean;
}

const MAX_CHIPS_VISIBLE = 1;

const FormulaChip: React.FC<React.PropsWithChildren<FormulaChipProps>> = ({
    items = [],
    showAll = false,
}) => {
    const sortedItems = useMemo(
        () => items?.sort((a, b) => a.name.localeCompare(b.name)),
        [items],
    );

    const allItemsString = useMemo<string>(
        () => sortedItems.map(({ name }) => name).join(', '),
        [sortedItems],
    );

    const displayItems = useMemo<string[]>(() => {
        if (sortedItems.length) {
            if (!showAll && sortedItems.length > MAX_CHIPS_VISIBLE) {
                return sortedItems
                    .slice(0, MAX_CHIPS_VISIBLE)
                    .map(({ name }) => name);
            }
            return [allItemsString];
        }
        return [];
    }, [sortedItems, showAll, allItemsString]);

    const remaining = useMemo<string[]>(() => {
        if (
            !showAll &&
            sortedItems.length &&
            sortedItems.length > MAX_CHIPS_VISIBLE
        ) {
            return sortedItems
                .slice(MAX_CHIPS_VISIBLE, sortedItems.length)
                .map(({ name }) => name);
        }
        return [];
    }, [sortedItems, showAll]);

    return (
        <Container>
            {displayItems.map((item) => {
                return (
                    <React.Fragment key={item}>
                        <AampeTooltip title={item ? item : ''}>
                            <ChipList>{item}</ChipList>
                        </AampeTooltip>
                    </React.Fragment>
                );
            })}
            {!!remaining.length && (
                <AampeTooltip title={remaining.join(',')}>
                    <ChipList>{`+${remaining.length}`}</ChipList>
                </AampeTooltip>
            )}
        </Container>
    );
};
const Container = styled.div({
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: aampeTheme.spacing(0.5),
});
const ChipList = styled.div(() => ({
    maxWidth: '110px',
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: '16px',
    padding: aampeTheme.spacing(0.5, 1.5),
    lineHeight: '18px',
    fontSize: '14px',
    color: aampeTheme.colors.gray800,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
}));

export default FormulaChip;
